/**
 * One prospect's panel (PRD §4.5, §11): the tier and its action, every reason with a citation back
 * to where in the CRM it came from and when it was read, anything recorded about existing LTC cover,
 * what is missing to score them better, and the feedback control.
 *
 * If the contact has become a Waterlily client or been invited since the last scoring pass, the panel
 * says so first, because the list will drop them on the next pass.
 *
 * A prospect that is no longer on the list — an old link, or someone who dropped off at the last
 * scoring pass — gets a panel that says so, and a load that failed can be tried again. Neither is left
 * looking like it is still loading.
 */
import { Alert, AlertDescription, Badge, Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton } from '@design-system';
import { ExternalLink, Info } from 'lucide-react';
import type { Prospecting } from '@client/api/types';
import { useProspectDetail } from '../hooks/use-prospect-detail';
import { useProspectFeedback } from '../hooks/use-prospect-feedback';
import { crmCopy, type CrmCopy } from '../lib/crm-copy';
import { FIT_BAND_LABELS, NOTE_LABELS, formatDate, fullName, priorityCopy } from '../lib/labels';
import { FeedbackControl } from './feedback-control';
import { PriorityBadge } from './priority-badge';

interface CitationProps {
  citation: Prospecting.ReasonCitation;
  readAt: string;
  copy: CrmCopy;
}

function Citation({ citation, readAt, copy }: CitationProps) {
  const source =
    citation.origin === 'field' && citation.sourceLabel
      ? `${citation.sourceLabel} (${citation.sourceField})`
      : citation.sourceField ?? (citation.origin === 'field' ? 'Field' : copy.origins[citation.origin]);
  return (
    <span className="text-xs text-gray-500">
      From {source}
      {citation.value ? ` · ${citation.value}` : ''} · read {formatDate(readAt)}
    </span>
  );
}

interface ProspectPanelProps {
  provider: Prospecting.Provider;
  prospectId: string | null;
  modelVersion: string;
  onClose: () => void;
}

export function ProspectPanel({ provider, prospectId, modelVersion, onClose }: ProspectPanelProps) {
  const { detail, isLoading, notFound, failed, retry } = useProspectDetail(provider, prospectId);
  const feedback = useProspectFeedback(provider, detail, modelVersion);
  const copy = crmCopy(provider);

  return (
    <Sheet open={!!prospectId} onOpenChange={open => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-6 overflow-y-auto pb-16 sm:max-w-lg">
        {notFound ? (
          <SheetHeader>
            <SheetTitle>This contact is not on your list</SheetTitle>
            <SheetDescription>
              They may have been removed from {copy.name}, or dropped off the list when your book was last scored.
            </SheetDescription>
          </SheetHeader>
        ) : failed ? (
          <SheetHeader>
            <SheetTitle>Something went wrong loading this contact</SheetTitle>
            <SheetDescription>Try again in a minute.</SheetDescription>
            <div className="pt-2">
              <Button variant="outline" onClick={retry}>
                Try again
              </Button>
            </div>
          </SheetHeader>
        ) : isLoading || !detail ? (
          <div className="flex flex-col gap-3 p-4">
            {/* The sheet needs a title for screen readers even while it loads. */}
            <SheetTitle className="sr-only">Loading prospect</SheetTitle>
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>{fullName(detail)}</SheetTitle>
              <SheetDescription>{detail.email}</SheetDescription>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <PriorityBadge tier={detail.tier} />
                {/* "Low fit" is already what an unprioritized contact's badge says. */}
                {detail.fitBand && detail.tier !== 'untiered' && (
                  <Badge variant="secondary">{FIT_BAND_LABELS[detail.fitBand]}</Badge>
                )}
                {detail.tierAction && <span className="text-sm text-gray-700">{detail.tierAction}</span>}
              </div>
            </SheetHeader>

            <div className="flex flex-col gap-6 px-4">
              {detail.alreadyClientNow && (
                <Alert variant="info">
                  <Info />
                  <AlertDescription>
                    This contact is now a Waterlily client or has been invited. They will leave this list the next
                    time your contacts are scored.
                  </AlertDescription>
                </Alert>
              )}

              {detail.exclusionLabel && <p className="text-sm text-gray-700">Not ranked: {detail.exclusionLabel}.</p>}
              {detail.insufficientLabel && <p className="text-sm text-gray-700">Not ranked: {detail.insufficientLabel}.</p>}
              {detail.untieredLabel && <p className="text-sm text-gray-700">Not ranked: {detail.untieredLabel}.</p>}

              {detail.reasons.length > 0 && (
                <section className="flex flex-col gap-3">
                  <h3 className="text-sm font-semibold text-gray-900">{priorityCopy.whyThis}</h3>
                  <ol className="flex flex-col gap-3">
                    {detail.reasons.map(reason => (
                      <li key={reason.code} className="flex flex-col gap-0.5">
                        <span className="text-sm text-gray-900">{reason.text}</span>
                        <Citation citation={reason.citation} readAt={reason.readAt} copy={copy} />
                      </li>
                    ))}
                  </ol>
                  {detail.crmUrl && (
                    <a
                      href={detail.crmUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      Open in {copy.name} <ExternalLink className="size-3" />
                    </a>
                  )}
                </section>
              )}

              {detail.notes.length > 0 && (
                <section className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-gray-900">Also recorded</h3>
                  {detail.notes.map(note => (
                    <span key={note} className="text-sm text-gray-700">
                      {note === 'ltcProductHeld' ? copy.ltcProductHeld : NOTE_LABELS[note] ?? note}
                    </span>
                  ))}
                </section>
              )}

              {detail.persona && (
                <section className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-gray-900">Most similar Waterlily planners</h3>
                  <span className="text-sm text-gray-700">{detail.persona.summary}</span>
                </section>
              )}

              {detail.coverage.missing.length > 0 && (
                <section className="flex flex-col gap-1">
                  <h3 className="text-sm font-semibold text-gray-900">Not recorded for this contact</h3>
                  <ul className="list-disc pl-4 text-sm text-gray-700">
                    {detail.coverage.missing.map(entry => (
                      <li key={entry.feature}>{entry.hint}</li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="border-t pt-4">
                <FeedbackControl
                  crmName={copy.name}
                  latest={detail.latestFeedback}
                  isSaving={feedback.isSaving}
                  error={feedback.error?.message ?? null}
                  onVote={feedback.vote}
                  onUndo={feedback.undo}
                />
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
